import React, { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { formatApiError } from "../auth/api";
import { Layers, Lock } from "lucide-react";

export default function LoginPage() {
  const { login, isAuthed, isChecking } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || "/";

  if (isAuthed && !isChecking) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      await login(username.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err, "Credenciales incorrectas"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="h-screen w-screen flex items-center justify-center bg-[#090A0B] text-[#F3F4F6] p-6"
      data-testid="login-page"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-[360px] flex flex-col gap-5 p-7 rounded-md border border-[#31373E] bg-[#1C1F24] shadow-2xl"
        data-testid="login-form"
      >
        <div className="flex items-center gap-2 mb-2">
          <Layers size={20} className="text-[#00E5FF]" strokeWidth={1.75} />
          <h1 className="text-base font-semibold tracking-tight">
            KAPEI Imposer <span className="text-[#00E5FF]">PRO</span>
          </h1>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]" htmlFor="login-username">
            Usuario
          </label>
          <input
            id="login-username"
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            data-testid="login-username-input"
            required
            autoFocus
            className="h-10 bg-[#121417] border border-[#31373E] text-sm px-3 rounded-sm font-mono text-[#F3F4F6] focus:outline-none focus:ring-1 focus:ring-[#00E5FF] focus:border-[#00E5FF]"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]" htmlFor="login-password">
            Contraseña
          </label>
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password-input"
            required
            className="h-10 bg-[#121417] border border-[#31373E] text-sm px-3 rounded-sm font-mono text-[#F3F4F6] focus:outline-none focus:ring-1 focus:ring-[#00E5FF] focus:border-[#00E5FF]"
          />
        </div>

        {error && (
          <div
            className="text-[11px] font-mono text-[#FF3B30] bg-[#2A1818] border border-[#5A2020] rounded-sm px-3 py-2"
            data-testid="login-error"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !username || !password}
          data-testid="login-submit-btn"
          className="h-10 rounded-sm bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Lock size={14} strokeWidth={2} />
          {loading ? "Comprobando…" : "Entrar"}
        </button>

        <p className="text-[10px] text-[#6B7280] text-center leading-relaxed">
          Acceso privado. El registro está deshabilitado.
        </p>
      </form>
    </div>
  );
}
